import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  ensureOwnOrganization,
  subscribeToMailingList,
} from '@/lib/queries/onboarding'
import { organizationInvitation } from '@/lib/queries/organization-invitations'
import { organizationMembership } from '@/lib/queries/organization-memberships'
import { organization } from '@/lib/queries/organizations'
import { workspace } from '@/lib/queries/workspaces'
import { readLastWorkspace } from '@/features/workspaces/lib/last-workspace'

export const Route = createFileRoute('/_authenticated/')({
  loader: async ({ context }) => {
    const { currentProfile } = context

    // Fetched fresh, not static: the redirect must reflect current memberships.
    const memberships = await context.queryClient.query(
      organizationMembership.findManyByUser(currentProfile.id)
    )

    if (memberships.length > 0) {
      const lastWorkspace = readLastWorkspace(currentProfile.id)
      const orgId =
        memberships.find((m) => m.org_id === lastWorkspace?.orgId)?.org_id ??
        memberships[0].org_id

      // CRITICAL: Verify membership with the EXACT query org route uses
      // This ensures cache consistency - if we find it, org route will too
      const membership = await context.queryClient.query({
        ...organizationMembership.findOneByUserAndOrg(currentProfile.id, orgId),
        staleTime: 'static',
      })

      // Only redirect if membership is confirmed
      if (membership) {
        void context.queryClient
          .query(organization.findOne(orgId))
          .catch(() => {})

        // RLS limits this to workspaces the user can open, so a remembered
        // workspace that was archived or made private falls through.
        const workspaces = await context.queryClient.query(
          workspace.findMany(orgId)
        )
        const landing =
          workspaces.find((w) => w.id === lastWorkspace?.workspaceId) ??
          (workspaces.length === 1 ? workspaces[0] : undefined)

        if (landing) {
          throw redirect({
            to: '/org/$orgId/workspace/$workspaceId',
            params: { orgId, workspaceId: landing.id },
          })
        }

        throw redirect({
          to: '/org/$orgId',
          params: { orgId },
        })
      }
    }

    // No organization yet: a new self-serve account, or an invitee who
    // hasn't accepted. The onboarding guard has already made sure of a name.
    if (memberships.length === 0 && currentProfile.full_name) {
      subscribeToMailingList(currentProfile.email, currentProfile.full_name)
    }

    // A pending invitation is accepted on the invitation page; otherwise the
    // user gets their own organization on the Free plan.
    const [invitation] = await context.queryClient.query(
      organizationInvitation.findManyPendingByUser(currentProfile.id)
    )

    if (invitation) {
      throw redirect({
        to: '/auth/accept-invite',
        search: { org_id: invitation.org_id },
      })
    }

    if (memberships.length === 0 && (await ensureOwnOrganization())) {
      await context.queryClient.invalidateQueries({
        queryKey: ['organization-memberships'],
      })
      throw redirect({ to: '/' })
    }

    throw new Error('Could not open your organization')
  },
})
